const TEMPLATE_BODY_ATTRIBUTE_SELECTOR = /^on/i;

export const prepareTemplateHtml = (templateHtml: string) => {
  let normalizedTemplate = templateHtml || "";
  normalizedTemplate = normalizedTemplate.replace(
    /<body style="margin: [^;]+;/,
    '<body style="'
  );
  normalizedTemplate = normalizedTemplate.replace(
    /<a\s+([^>]*\s)?href="([^"]*)"(?![^>]*target)/g,
    '<a $1href="$2" target="_blank"'
  );

  return normalizedTemplate.trim();
};

const copyBodyAttributesToRoot = (body: HTMLElement, root: HTMLElement) => {
  Array.from(body.attributes).forEach((attribute) => {
    if (attribute.name === "style" || TEMPLATE_BODY_ATTRIBUTE_SELECTOR.test(attribute.name)) {
      return;
    }

    root.setAttribute(attribute.name, attribute.value);
  });

  const bodyStyle = (body.getAttribute("style") || "").trim();
  if (bodyStyle) {
    root.setAttribute("style", bodyStyle);
  }
};

const applyCssTextToElement = (element: HTMLElement, cssText: string) => {
  if (!cssText.trim()) return;

  const tempElement = element.ownerDocument.createElement("div");
  tempElement.style.cssText = cssText;

  for (let index = 0; index < tempElement.style.length; index += 1) {
    const propertyName = tempElement.style[index];
    element.style.setProperty(
      propertyName,
      tempElement.style.getPropertyValue(propertyName),
      tempElement.style.getPropertyPriority(propertyName)
    );
  }
};

const parseStyleRules = (cssText: string) => {
  if (!cssText.trim() || typeof document === "undefined") return [];

  const styleElement = document.createElement("style");
  styleElement.textContent = cssText;

  const parentNode = document.head || document.documentElement;
  parentNode.appendChild(styleElement);

  const rules = styleElement.sheet ? Array.from(styleElement.sheet.cssRules) : [];
  styleElement.remove();

  return rules;
};

const inlineStyleRule = (
  rule: CSSStyleRule,
  parsedDocument: Document,
  rootElement: HTMLElement
) => {
  const selectors = rule.selectorText
    .split(",")
    .map((selector) => selector.trim())
    .filter(Boolean);

  selectors.forEach((selector) => {
    if (selector === "body" || selector === "html" || selector === ":root") {
      applyCssTextToElement(rootElement, rule.style.cssText);
      return;
    }

    let matchedElements: Element[] = [];
    try {
      matchedElements = Array.from(parsedDocument.querySelectorAll(selector));
    } catch {
      return;
    }

    matchedElements.forEach((element) => {
      if (element instanceof HTMLElement) {
        applyCssTextToElement(element, rule.style.cssText);
      }
    });
  });
};

const inlineTemplateStyles = (
  parsedDocument: Document,
  rootElement: HTMLElement,
  { preserveStyleTags = false }: { preserveStyleTags?: boolean } = {}
) => {
  const styleTags = Array.from(parsedDocument.querySelectorAll("style"));
  const preservedStylesHtml = preserveStyleTags
    ? styleTags.map((styleTag) => styleTag.outerHTML).join("")
    : "";

  styleTags.forEach((styleTag) => {
    const cssRules = parseStyleRules(styleTag.textContent || "");

    cssRules.forEach((rule) => {
      if (typeof CSSStyleRule !== "undefined" && rule instanceof CSSStyleRule) {
        inlineStyleRule(rule, parsedDocument, rootElement);
      }
    });

    styleTag.remove();
  });

  if (preserveStyleTags && preservedStylesHtml) {
    rootElement.insertAdjacentHTML("afterbegin", preservedStylesHtml);
  }
};

const makeElementFluid = (element: Element) => {
  const widthAttr = element.getAttribute("width");
  if (widthAttr && /^\d+(\.\d+)?$/.test(widthAttr.trim())) {
    element.setAttribute("width", "100%");
  }

  if (element instanceof HTMLElement) {
    const currentWidth = element.style.width?.trim();
    if (currentWidth && /^\d+(\.\d+)?px$/i.test(currentWidth)) {
      element.style.width = "100%";
    }
    element.style.maxWidth = "100%";
    element.style.boxSizing = "border-box";
  }
};

export const buildTemplateBodyHtml = (
  templateHtml: string,
  {
    fluid = false,
    preserveStyleTags = false,
  }: { fluid?: boolean; preserveStyleTags?: boolean } = {}
) => {
  const normalizedTemplate = prepareTemplateHtml(templateHtml);
  if (!normalizedTemplate) return "";

  try {
    const parsedTemplate = new DOMParser().parseFromString(normalizedTemplate, "text/html");
    parsedTemplate.querySelectorAll("script").forEach((scriptTag) => scriptTag.remove());

    const body = parsedTemplate.body;
    const rootElement = parsedTemplate.createElement("div");
    rootElement.setAttribute("data-template-root", "email");
    copyBodyAttributesToRoot(body, rootElement);
    inlineTemplateStyles(parsedTemplate, rootElement, { preserveStyleTags });

    if (fluid) {
      rootElement.style.margin = "0";
      rootElement.style.width = "100%";
      rootElement.style.maxWidth = "100%";
      rootElement.style.boxSizing = "border-box";

      const firstContainer = body.querySelector("table, div, section, article");
      if (firstContainer) {
        makeElementFluid(firstContainer);
      }

      body.querySelectorAll("table, div, section, article, td").forEach((element) => {
        makeElementFluid(element);
      });

      body.querySelectorAll("img").forEach((image) => {
        if (image instanceof HTMLElement) {
          image.style.maxWidth = "100%";
          image.style.height = "auto";
        }
      });
    }

    rootElement.insertAdjacentHTML("beforeend", body.innerHTML.trim());
    return rootElement.outerHTML.trim();
  } catch (error) {
    console.error("Error building template HTML:", error);
    return normalizedTemplate;
  }
};

export const escapeHtmlText = (rawText: string) => {
  return (rawText || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};
